import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { skillAPI } from 'api/SkillAPI'
import { useUser } from 'hooks'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { IIndustry, IRole, ISkill } from 'types/levelItemInterfaces'
import { IError } from 'types/error'
import { chooseDBTranslation } from 'utils/translations'
import { fuseFiltering } from 'utils/utils'
import { ICertificate, ICompetence } from 'types/cvsInterfaces'
import { certificateAPI } from 'api/CertificateAPI'
import { competenceAPI } from 'api/CompetenceAPI'
import { isEqual } from 'lodash'

/** @notExported */
interface IFilterFieldsProps {
  /** A function to set the skill filter. */
  setSkillFilter: (values: ISkill[]) => void
  /** A function to set the role filter. */
  setRoleFilter: (values: IRole[]) => void
  /** A function to set the industry filter. */
  setIndustryFilter: (values: IIndustry[]) => void
  /** A function to set the certificate filter. */
  setCertificateFilter: (values: ICertificate[]) => void
  /** A function to set the competence filter. */
  setCompetenceFilter: (values: ICompetence[]) => void
  /** Should all items be shown  */
  allItems: boolean
  /** A function to set all items. */
  setAllItems: (values: boolean) => void
}

/**
 * FilterFields component.
 *
 * @param setSkillFilter - A function to set the skill filter.
 * @param setRoleFilter - A function to set the role filter.
 * @param setIndustryFilter - A function to set the industry filter.
 * @param setCertificateFilter - A function to set the certificate filter.
 * @param setCompetenceFilter - A function to set the competence filter.
 * @returns The FilterFields component.
 * @notExported
 */
const FilterFields: React.FC<IFilterFieldsProps> = ({
  setSkillFilter,
  setRoleFilter,
  setIndustryFilter,
  setCertificateFilter,
  setCompetenceFilter,
  allItems,
  setAllItems,
}) => {
  const { t, i18n } = useTranslation()
  const { groups, features } = useUser()

  const [backendError, setBackendError] = useState<IError>()
  const [skillOptions, setSkillOptions] = useState<ISkill[]>([])
  const [roleOptions, setRoleOptions] = useState<IRole[]>([])
  const [industryOptions, setIndustryOptions] = useState<IIndustry[]>([])
  const [certificateOptions, setCertificateOptions] = useState<ICertificate[]>([])
  const [competenceOptions, setCompetenceOptions] = useState<ICompetence[]>([])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        const skills = skillOptions.length > 0 ? skillOptions : await skillAPI.getAllSkills(controller)
        const roles = roleOptions.length > 0 ? roleOptions : await skillAPI.getAllRoles(controller)
        const industries = industryOptions.length > 0 ? industryOptions : await skillAPI.getAllIndustries(controller)
        const certificates =
          certificateOptions.length > 0 ? certificateOptions : await certificateAPI.getAll(controller)
        let competences: ICompetence[] = []
        if (features.includes('competences')) {
          competences = competenceOptions.length > 0 ? competenceOptions : await competenceAPI.getAll(controller)
        }
        {
          if (!isEqual(skills, skillOptions)) setSkillOptions(skills)
          if (!isEqual(roles, roleOptions)) setRoleOptions(roles)
          if (!isEqual(industries, industryOptions)) setIndustryOptions(industries)
          if (!isEqual(certificates, certificateOptions)) setCertificateOptions(certificates)
          if (features.includes('competences') && !isEqual(competences, competenceOptions))
            setCompetenceOptions(competences)
        }
      } catch (error) {
        setBackendError(error as IError)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [certificateOptions, competenceOptions, industryOptions, roleOptions, skillOptions, features])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  const getFilterButton = () => {
    return (
      <FormControlLabel
        control={
          <Checkbox
            data-testid="checkbox-team-filter-show-all"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setAllItems(event.target.checked)
            }}
            checked={allItems}
          />
        }
        label={t(`team.modal.showAll`)}
      />
    )
  }

  return (
    <Grid size={12} container spacing={1}>
      <Grid container size={12}>
        <Grid size={12}>
          <Typography variant="h5" fontWeight="bold">
            {t('team.filters')}
          </Typography>
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 4,
            lg: 2.4,
          }}
        >
          <Autocomplete
            data-testid="autocomplete-team-filter-skills"
            onChange={(_event, newValue) => {
              if (newValue) {
                setSkillFilter(newValue)
              }
            }}
            filterOptions={(options, { inputValue }) => fuseFiltering(options, inputValue, ['translations.name'])}
            clearOnBlur
            handleHomeEndKeys
            options={skillOptions}
            getOptionLabel={option => chooseDBTranslation(i18n, option).name}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {chooseDBTranslation(i18n, option).name}
                </li>
              )
            }}
            renderInput={params => (
              <InputField
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: params?.inputProps,
                  input: params?.InputProps,
                }}
                {...params}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { padding: '3px' } }}
                variant="outlined"
                label={t('team.skillPicker')}
                insetLabel
                margin="dense"
                size="small"
              />
            )}
            fullWidth
            multiple
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 4,
            lg: 2.4,
          }}
        >
          <Autocomplete
            data-testid="autocomplete-team-filter-roles"
            onChange={(_event, newValue) => {
              if (newValue) {
                setRoleFilter(newValue)
              }
            }}
            filterOptions={(options, { inputValue }) => fuseFiltering(options, inputValue, ['translations.name'])}
            clearOnBlur
            handleHomeEndKeys
            options={roleOptions}
            getOptionLabel={option => chooseDBTranslation(i18n, option).name}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {chooseDBTranslation(i18n, option).name}
                </li>
              )
            }}
            renderInput={params => (
              <InputField
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: params?.inputProps,
                  input: params?.InputProps,
                }}
                {...params}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { padding: '3px' } }}
                variant="outlined"
                label={t('team.rolePicker')}
                insetLabel
                margin="dense"
                size="small"
              />
            )}
            fullWidth
            multiple
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 4,
            lg: 2.4,
          }}
        >
          <Autocomplete
            data-testid="autocomplete-team-filter-industries"
            onChange={(_event, newValue) => {
              if (newValue) {
                setIndustryFilter(newValue)
              }
            }}
            filterOptions={(options, { inputValue }) => fuseFiltering(options, inputValue, ['translations.name'])}
            clearOnBlur
            handleHomeEndKeys
            options={industryOptions}
            getOptionLabel={option => chooseDBTranslation(i18n, option).name}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {chooseDBTranslation(i18n, option).name}
                </li>
              )
            }}
            renderInput={params => (
              <InputField
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: params?.inputProps,
                  input: params?.InputProps,
                }}
                {...params}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { padding: '3px' } }}
                variant="outlined"
                label={t('team.industryPicker')}
                insetLabel
                margin="dense"
                size="small"
              />
            )}
            fullWidth
            multiple
          />
        </Grid>
        <Grid
          size={{
            xs: 6,
            md: 4,
            lg: 2.5,
          }}
        >
          <Autocomplete
            data-testid="autocomplete-team-filter-certificates"
            onChange={(_event, newValue) => {
              if (newValue) {
                setCertificateFilter(newValue)
              }
            }}
            filterOptions={(options, { inputValue }) =>
              fuseFiltering(options, inputValue, ['translations.certificate'])
            }
            clearOnBlur
            handleHomeEndKeys
            options={certificateOptions}
            getOptionLabel={option => chooseDBTranslation(i18n, option).certificate}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {chooseDBTranslation(i18n, option).certificate}
                </li>
              )
            }}
            renderInput={params => (
              <InputField
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: params?.inputProps,
                  input: params?.InputProps,
                }}
                {...params}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { padding: '3px' } }}
                variant="outlined"
                label={t('team.certificatePicker')}
                insetLabel
                margin="dense"
                size="small"
              />
            )}
            fullWidth
            multiple
          />
        </Grid>
        {features.includes('competences') && (
          <Grid
            size={{
              xs: 6,
              md: 4,
              lg: 2.5,
            }}
          >
            <Autocomplete
              data-testid="autocomplete-team-filter-competences"
              onChange={(_event, newValue) => {
                if (newValue) {
                  setCompetenceFilter(newValue)
                }
              }}
              filterOptions={(options, { inputValue }) => fuseFiltering(options, inputValue, ['translations.name'])}
              clearOnBlur
              handleHomeEndKeys
              options={competenceOptions}
              getOptionLabel={option => chooseDBTranslation(i18n, option).name}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {chooseDBTranslation(i18n, option).name}
                  </li>
                )
              }}
              renderInput={params => (
                <InputField
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: params?.inputProps,
                    input: params?.InputProps,
                  }}
                  {...params}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { padding: '3px' } }}
                  variant="outlined"
                  label={t('team.competencePicker')}
                  insetLabel
                  margin="dense"
                  size="small"
                />
              )}
              fullWidth
              multiple
            />
          </Grid>
        )}
      </Grid>
      {groups.includes('admin') && <Grid size="auto">{getFilterButton()}</Grid>}
    </Grid>
  )
}

export default FilterFields
